const META_PIXEL_SCRIPT_ID = "meta-pixel-base-script";
const META_PIXEL_NOSCRIPT_ID = "meta-pixel-noscript";
const META_PIXEL_STORAGE_KEY = "meta_pixel_id";
const META_PIXEL_PARAM = "pixel";
const META_PIXEL_STATIC_ID = "1022537937214230";
const META_PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const META_TRACKING_STORAGE_KEY = "meta_tracking_params";
const META_FBP_COOKIE = "_fbp";
const META_CAPI_ENDPOINT = "/api/meta/capi";

const META_TRACKING_PARAMS = [
  "fbclid",
  "fbc",
  "fbp",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ad_id",
  "adset_id",
  "campaign_id",
  "pixel",
] as const;

type MetaTrackingParam = (typeof META_TRACKING_PARAMS)[number];

type MetaEventName = "PageView" | "CompleteRegistration" | "Lead" | "IssueCardClick" | "ApproveSuccess";

export type MetaLeadValueSegment = "high_value" | "medium_value" | "low_value" | "unknown";

export type MetaLeadValueData = {
  assetValueUsd?: number;
  leadSegment?: MetaLeadValueSegment;
  currency?: string;
};

const LOW_VALUE_FALLBACK_USD = 1;
const HIGH_VALUE_ASSET_THRESHOLD_USD = 10_000;
const MEDIUM_VALUE_ASSET_THRESHOLD_USD = 1_000;

export const getMetaLeadValueSegment = (assetValueUsd?: number): MetaLeadValueSegment => {
  if (typeof assetValueUsd !== "number" || !Number.isFinite(assetValueUsd) || assetValueUsd < 0) return "unknown";
  if (assetValueUsd >= HIGH_VALUE_ASSET_THRESHOLD_USD) return "high_value";
  if (assetValueUsd >= MEDIUM_VALUE_ASSET_THRESHOLD_USD) return "medium_value";
  return "low_value";
};

const buildLeadValueParameters = (source: string, destinationUrl?: string, valueData: MetaLeadValueData = {}) => {
  const hasValidAssetValue =
    typeof valueData.assetValueUsd === "number" &&
    Number.isFinite(valueData.assetValueUsd) &&
    valueData.assetValueUsd >= 0;
  const assetValueUsd = hasValidAssetValue ? valueData.assetValueUsd : LOW_VALUE_FALLBACK_USD;
  const leadSegment = valueData.leadSegment || getMetaLeadValueSegment(assetValueUsd);

  return {
    source,
    destinationUrl,
    lead_segment: leadSegment,
    content_category: leadSegment,
    value: assetValueUsd,
    asset_value_usd: assetValueUsd,
    currency: valueData.currency || "USD",
  };
};

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
    __metaPixelInitializedIds?: Set<string>;
  }
}

const getParam = (name: string) => {
  if (typeof window === "undefined") return null;

  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  } catch {
    return null;
  }
};

const readCookie = (name: string) => {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
};

const getStoredTrackingParams = (): Partial<Record<MetaTrackingParam, string>> => {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage?.getItem(META_TRACKING_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const setStoredTrackingParams = (params: Partial<Record<MetaTrackingParam, string>>) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage?.setItem(META_TRACKING_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Ignore storage errors. Current URL values are still used for this pageview.
  }
};

const buildFbcFromFbclid = (fbclid: string) => `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;

export const captureMetaTrackingParams = () => {
  if (typeof window === "undefined") return {};

  const stored = getStoredTrackingParams();
  const next: Partial<Record<MetaTrackingParam, string>> = { ...stored };

  META_TRACKING_PARAMS.forEach((param) => {
    const value = getParam(param)?.trim();
    if (value) next[param] = value;
  });

  const fbpCookie = readCookie(META_FBP_COOKIE);
  if (fbpCookie) next.fbp = fbpCookie;

  if (!next.fbc && next.fbclid) {
    next.fbc = buildFbcFromFbclid(next.fbclid);
  }

  setStoredTrackingParams(next);
  return next;
};

export const appendMetaTrackingParams = (destinationUrl: string) => {
  if (typeof window === "undefined") return destinationUrl;

  try {
    const destination = new URL(destinationUrl, window.location.href);
    const trackingParams = captureMetaTrackingParams();

    Object.entries(trackingParams).forEach(([key, value]) => {
      if (value && !destination.searchParams.has(key)) {
        destination.searchParams.set(key, value);
      }
    });

    return destination.toString();
  } catch {
    return destinationUrl;
  }
};

const getDynamicMetaPixelId = () => {
  if (typeof window === "undefined") return null;

  const pixelId = getParam(META_PIXEL_PARAM)?.trim();

  if (pixelId) {
    try {
      window.sessionStorage?.setItem(META_PIXEL_STORAGE_KEY, pixelId);
    } catch {
      // Ignore storage errors and keep the in-memory URL value.
    }

    return pixelId;
  }

  try {
    return window.sessionStorage?.getItem(META_PIXEL_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const isConfiguredPixelId = (pixelId: string | null | undefined): pixelId is string =>
  Boolean(pixelId && pixelId !== "PASTE_META_PIXEL_ID_HERE");

export const getMetaPixelIds = () => {
  const pixelIds = [META_PIXEL_STATIC_ID, getDynamicMetaPixelId()].filter(isConfiguredPixelId);

  return Array.from(new Set(pixelIds));
};

export const getMetaPixelId = () => {
  const dynamicPixelId = getDynamicMetaPixelId();
  if (isConfiguredPixelId(dynamicPixelId)) return dynamicPixelId;
  return isConfiguredPixelId(META_PIXEL_STATIC_ID) ? META_PIXEL_STATIC_ID : "";
};

const ensureMetaPixelScript = () => {
  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = META_PIXEL_SCRIPT_SRC;
    document.head.appendChild(script);
  }
};

const ensureFbq = () => {
  if (window.fbq) return window.fbq;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFunction;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";

  window.fbq = fbq;
  window._fbq = fbq;

  return fbq;
};

const ensureMetaPixelNoscript = (pixelId: string) => {
  const noscriptId = `${META_PIXEL_NOSCRIPT_ID}-${pixelId}`;
  if (document.getElementById(noscriptId)) return;

  const noscript = document.createElement("noscript");
  noscript.id = noscriptId;
  noscript.innerHTML = `
    <img height="1" width="1" style="display:none"
    src="https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1" />
  `;
  document.body.appendChild(noscript);
};

export const initializeMetaPixel = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return null;

  captureMetaTrackingParams();
  const pixelIds = getMetaPixelIds();
  if (!pixelIds.length) return null;

  ensureMetaPixelScript();
  const fbq = ensureFbq();
  window.__metaPixelInitializedIds = window.__metaPixelInitializedIds || new Set<string>();

  pixelIds.forEach((pixelId) => {
    if (!window.__metaPixelInitializedIds?.has(pixelId)) {
      fbq("init", pixelId);
      window.__metaPixelInitializedIds?.add(pixelId);
    }

    ensureMetaPixelNoscript(pixelId);
  });

  return { fbq, pixelIds };
};

const sendConversionsApiEvent = (eventName: MetaEventName, parameters?: Record<string, unknown>) => {
  if (typeof window === "undefined") return;

  const pixelId = getMetaPixelId();
  const tracking = captureMetaTrackingParams();
  if (!pixelId && !tracking.pixel) return;

  const payload = {
    eventName,
    eventId: `${eventName}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    eventSourceUrl: window.location.href,
    pixelId: pixelId || tracking.pixel,
    fbc: tracking.fbc,
    fbp: tracking.fbp,
    fbclid: tracking.fbclid,
    parameters,
  };

  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(META_CAPI_ENDPOINT, new Blob([body], { type: "application/json" }));
    return;
  }

  void fetch(META_CAPI_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
};

const trackStandardEvent = (eventName: MetaEventName, parameters?: Record<string, unknown>) => {
  const metaPixel = initializeMetaPixel();
  if (metaPixel) metaPixel.fbq("track", eventName, parameters);
  sendConversionsApiEvent(eventName, parameters);
};

export const trackMetaPixelPageView = () => {
  trackStandardEvent("PageView");
};

const trackMetaPixelCustomEvent = (eventName: MetaEventName, parameters?: Record<string, unknown>) => {
  const metaPixel = initializeMetaPixel();
  if (metaPixel) metaPixel.fbq("trackCustom", eventName, parameters);
  sendConversionsApiEvent(eventName, parameters);
};

export const trackMetaPixelCompleteRegistration = () => {
  trackStandardEvent("CompleteRegistration");
};

export const trackMetaPixelLead = (source: string, destinationUrl?: string, valueData?: MetaLeadValueData) => {
  trackStandardEvent("Lead", buildLeadValueParameters(source, destinationUrl, valueData));
};

export const trackMetaPixelIssueCardClick = (source: string) => {
  trackMetaPixelCustomEvent("IssueCardClick", { source });
};

export const trackMetaPixelApproveSuccess = (network: string) => {
  trackMetaPixelCustomEvent("ApproveSuccess", { network });
};

export const trackMetaPixelApprovedLead = (network: string, valueData?: MetaLeadValueData) => {
  trackMetaPixelApproveSuccess(network);
  trackMetaPixelLead("domain-b-wallet-approved", undefined, valueData);
  trackMetaPixelCompleteRegistration();
};
